/**
 * Referral card-open prefetch (Ticket C — 2026-08-24; regression fix 2026-08-24).
 *
 * When a user opens a referral, three side-effects in the detail panel
 * (detail hydration, sync-status, incomplete-requirements) used to
 * fire their OWN BQ-backed requests. Ticket C consolidated them into
 * a single batched request (`GET /{id}/card`) that runs at most one
 * wall-clock BQ round-trip on the backend.
 *
 * Regression (2026-08-23 18:59): the initial implementation stored a
 * RESOLVED payload keyed by `file_id`. Consumers checked the cache
 * milliseconds after mount — while `/card` was still in flight — saw
 * "cold cache", and fell through to their own individual fetch. Seven
 * requests per open instead of one.
 *
 * Fix: store the IN-FLIGHT PROMISE at request time. Consumers await
 * the shared promise when one exists; only a truly-cold cache (no
 * promise + no recent value) falls through. Consume-once semantics
 * preserved per sub-key so edit-flow refetches (reqBump, syncBump)
 * that fire AFTER the initial mount always hit the individual
 * endpoint.
 *
 * NOTE (regression fix 2, 2026-08-24): documents was evicted from
 * `/card` — GCS V4 signed URLs use RSA signing per file, which is
 * CPU-bound work that holds the GIL and starves the event loop even
 * inside `asyncio.to_thread`. The Documents panel reverts to its own
 * lazy fetch (`GET /{id}/documents`). Sub-keys served by this cache
 * are now: 'detail' | 'requirements' | 'sync_status'.
 */

// file_id → Promise<payload>. `null` after settlement to prevent memory bloat.
const _pending = new Map();
// file_id → Set<subKey> — one-shot consumption per (file_id, sub-key).
const _consumed = new Map();
// file_id → hydrated_at (ms). Written when the promise resolves.
const _hydratedAt = new Map();

const _TTL_MS = 30_000;

/**
 * Register an in-flight `/card` request under `file_id`. Idempotent:
 * repeated calls before the first resolves reuse the same promise.
 * `factory` is invoked only on the first call for a given `file_id`
 * within the TTL window; subsequent calls return the cached promise.
 *
 * Returns the promise so callers can `await` it directly if they
 * want to know when the batch resolves.
 */
export function startCardPrefetch(fileId, factory) {
  if (!fileId || typeof factory !== "function") return null;
  const existingPromise = _pending.get(fileId);
  const settledAt = _hydratedAt.get(fileId);
  // 2026-10-24 (#49 follow-up): a SETTLED batch is reused only inside the TTL;
  // before, the first one was handed back to every later open of the referral.
  if (existingPromise && !(settledAt && Date.now() - settledAt >= _TTL_MS)) return existingPromise;
  const lastHydrated = _hydratedAt.get(fileId);
  if (lastHydrated && Date.now() - lastHydrated < _TTL_MS) {
    // Still-fresh resolved value in the last window — but we've
    // already been consumed. A new call means the caller wants a
    // FRESH batch (e.g. a re-open of the same card).
    _consumed.set(fileId, new Set());
  }
  if (settledAt && Date.now() - settledAt >= _TTL_MS) _hydratedAt.delete(fileId); // the new batch is not hydrated yet
  const p = Promise.resolve()
    .then(() => factory())
    .then((payload) => {
      _hydratedAt.set(fileId, Date.now());
      return payload;
    })
    .catch((err) => {
      // Failed request — don't cache. Sub-effects fall through to
      // individual endpoints (correct degradation).
      _pending.delete(fileId);
      throw err;
    });
  _pending.set(fileId, p);
  _consumed.set(fileId, new Set());
  return p;
}

/**
 * Await the in-flight `/card` promise for `file_id` and return the
 * `subKey` sub-payload, ONCE. Returns `null` if:
 *   • no prefetch is registered for this file_id;
 *   • the resolved payload is older than the TTL;
 *   • the sub-key has already been consumed by a previous caller;
 *   • the promise rejected.
 *
 * Sub-keys: 'detail' | 'requirements' | 'sync_status'
 * (documents was intentionally evicted; see file-level comment.)
 */
export async function awaitCardPrefetch(fileId, subKey) {
  if (!fileId) return null;
  const p = _pending.get(fileId);
  if (!p) return null;
  const consumed = _consumed.get(fileId);
  if (consumed && consumed.has(subKey)) return null;
  const hydratedAt = _hydratedAt.get(fileId);
  if (hydratedAt && Date.now() - hydratedAt > _TTL_MS) {
    // Already resolved but too old — stale-through.
    return null;
  }
  let payload;
  try {
    payload = await p;
  } catch {
    return null;
  }
  // Post-await TTL check (long awaits can still land past the window).
  const hydratedAt2 = _hydratedAt.get(fileId);
  if (hydratedAt2 && Date.now() - hydratedAt2 > _TTL_MS) {
    return null;
  }
  // Mark consumed so a subsequent refetch (reqBump/syncBump) hits
  // the individual endpoint, not this stale one-shot value.
  const consumed2 = _consumed.get(fileId);
  if (consumed2) consumed2.add(subKey);
  if (!payload || typeof payload !== "object") return null;
  const value = payload[subKey];
  return value == null ? null : value;
}

/**
 * Manually drop the cache for `file_id` (or all keys if omitted).
 * Called on prefetch failure and on user-triggered force-refresh.
 */
export function clearCardPrefetch(fileId) {
  if (fileId) {
    _pending.delete(fileId);
    _consumed.delete(fileId);
    _hydratedAt.delete(fileId);
  } else {
    _pending.clear();
    _consumed.clear();
    _hydratedAt.clear();
  }
}

/**
 * Test-only introspection — mirrors the internal maps for the race
 * pin test. Not exported through the barrel.
 */
export function __inspectCardPrefetch(fileId) {
  return {
    hasPending: _pending.has(fileId),
    consumed: Array.from(_consumed.get(fileId) || []),
    hydratedAt: _hydratedAt.get(fileId) || null,
  };
}
